import { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck, SignIn, Lock, UserPlus, UserMinus, Trash, EnvelopeSimple,
  CreditCard, CheckCircle, FunnelSimple, DownloadSimple, MagnifyingGlass, Sparkle, FilePdf,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const ACTION_META = {
  login: { label: "Signed in", icon: SignIn, cls: "text-sky-600 bg-sky-50" },
  login_locked: { label: "Account locked", icon: Lock, cls: "text-alert bg-alert/10" },
  member_added: { label: "Member added", icon: UserPlus, cls: "text-emerald-600 bg-emerald-50" },
  member_invited: { label: "Member invited", icon: EnvelopeSimple, cls: "text-primary bg-primary/5" },
  member_removed: { label: "Member removed", icon: UserMinus, cls: "text-alert bg-alert/10" },
  invite_revoked: { label: "Invite revoked", icon: Trash, cls: "text-amber-600 bg-amber-50" },
  invite_accepted: { label: "Invite accepted", icon: CheckCircle, cls: "text-emerald-600 bg-emerald-50" },
  grantee_created: { label: "Grantee created", icon: UserPlus, cls: "text-emerald-600 bg-emerald-50" },
  grantee_invited: { label: "Grantee invited", icon: EnvelopeSimple, cls: "text-primary bg-primary/5" },
  grantee_updated: { label: "Grantee updated", icon: UserPlus, cls: "text-sky-600 bg-sky-50" },
  grantee_deleted: { label: "Grantee revoked", icon: UserMinus, cls: "text-alert bg-alert/10" },
  contract_deleted: { label: "Contract deleted", icon: Trash, cls: "text-alert bg-alert/10" },
  contract_status_changed: { label: "Contract status", icon: ShieldCheck, cls: "text-sky-600 bg-sky-50" },
  workflow_advanced: { label: "Workflow", icon: ShieldCheck, cls: "text-violet-600 bg-violet-50" },
  document_deleted: { label: "Document deleted", icon: Trash, cls: "text-alert bg-alert/10" },
  document_reviewed: { label: "Document reviewed", icon: CheckCircle, cls: "text-emerald-600 bg-emerald-50" },
  reimbursement_reviewed: { label: "Reimbursement", icon: CheckCircle, cls: "text-emerald-600 bg-emerald-50" },
  role_changed: { label: "Role changed", icon: UserPlus, cls: "text-amber-600 bg-amber-50" },
  password_reset: { label: "Password reset", icon: Lock, cls: "text-amber-600 bg-amber-50" },
  plan_changed: { label: "Plan changed", icon: CreditCard, cls: "text-primary bg-gold/15" },
  addon_activated: { label: "Add-on activated", icon: Sparkle, cls: "text-primary bg-gold/15" },
  audit_retention_changed: { label: "Retention changed", icon: ShieldCheck, cls: "text-sky-600 bg-sky-50" },
  audit_purged: { label: "Log purged", icon: Trash, cls: "text-amber-600 bg-amber-50" },
};

const FILTERS = [
  { value: "all", label: "All activity" },
  { value: "login", label: "Sign-ins" },
  { value: "login_locked", label: "Lockouts" },
  { value: "grantee_created", label: "Grantee created" },
  { value: "grantee_deleted", label: "Grantee revoked" },
  { value: "contract_status_changed", label: "Contract status" },
  { value: "document_reviewed", label: "Document reviews" },
  { value: "reimbursement_reviewed", label: "Reimbursement reviews" },
  { value: "role_changed", label: "Role changes" },
  { value: "contract_deleted", label: "Contract deleted" },
  { value: "document_deleted", label: "Document deleted" },
  { value: "plan_changed", label: "Plan changes" },
];

const RETENTION_LABELS = { 0: "Keep forever", 30: "30 days", 90: "90 days", 180: "180 days", 365: "365 days" };
const PAGE = 50;

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AuditLog() {
  const [entitled, setEntitled] = useState(null);   // null=checking, false=needs add-on, true=ok
  const [level, setLevel] = useState("full");        // "basic" (Enterprise-included) | "full"
  const [pricing, setPricing] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const isFull = level === "full";

  useEffect(() => { api.get("/billing/addons").then(({ data }) => setPricing(data)).catch(() => {}); }, []);

  const params = useCallback((skip) => {
    const p = new URLSearchParams();
    if (filter !== "all") p.set("action", filter);
    if (search) p.set("q", search);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    p.set("skip", skip); p.set("limit", PAGE);
    return p.toString();
  }, [filter, search, start, end]);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit?${params(skip)}`);
      setEntitled(true);
      setLevel(data.level || "full");
      setTotal(data.total);
      setHasMore(data.has_more);
      setRows((prev) => (skip === 0 ? data.rows : [...prev, ...data.rows]));
    } catch (e) {
      if (e.response?.status === 402) setEntitled(false);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [filter, search, start, end]);

  const exportFile = (kind) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const path = kind === "pdf" ? "/api/audit/export/pdf" : "/api/audit/export";
    window.open(`${API}${path}?${params(0)}`, "_blank");
  };

  const buyAddon = async (cycle) => {
    setBusy(cycle);
    try {
      const { data } = await api.post("/billing/addon/checkout", { addon: "audit_addon", cycle, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(""); }
  };

  if (entitled === false) return <Upsell onBuy={buyAddon} busy={busy} pricing={pricing} />;

  return (
    <div>
      <PageHeader
        overline="Security · Audit & Compliance"
        title="Audit Log"
        actions={
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="audit-filter" className="w-48"><FunnelSimple size={15} className="mr-1.5" /><SelectValue /></SelectTrigger>
              <SelectContent>
                {FILTERS.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`audit-filter-${f.value}`}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isFull && (
              <>
                <Button variant="outline" data-testid="audit-export-btn" onClick={() => exportFile("csv")} disabled={total === 0}>
                  <DownloadSimple size={16} className="mr-1.5" /> CSV
                </Button>
                <Button variant="outline" data-testid="audit-export-pdf-btn" onClick={() => exportFile("pdf")} disabled={total === 0}>
                  <FilePdf size={16} className="mr-1.5" /> PDF report
                </Button>
              </>
            )}
          </div>
        }
      >
        A record of sensitive activity in your organization — sign-ins, lockouts, team & grantee changes, status &
        review changes, deletions and billing.{isFull ? " Search, filter, export and set retention below." : ""}
      </PageHeader>

      {!isFull && <BasicUpgradeBanner onBuy={buyAddon} busy={busy} pricing={pricing} />}
      {isFull && <RetentionPanel />}

      {/* search + date range (full only) */}
      {isFull && (
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <form className="flex-1 min-w-[240px] flex gap-2" onSubmit={(e) => { e.preventDefault(); setSearch(q); }}>
          <div className="relative flex-1">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actor or description…" className="pl-8" />
          </div>
          <Button type="submit" variant="outline" data-testid="audit-search-btn">Search</Button>
        </form>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">From</div>
            <Input data-testid="audit-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">To</div>
            <Input data-testid="audit-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
          </div>
          {(start || end || search || filter !== "all") && (
            <Button variant="ghost" data-testid="audit-clear" onClick={() => { setStart(""); setEnd(""); setQ(""); setSearch(""); setFilter("all"); }}>Clear</Button>
          )}
        </div>
      </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading activity…</div>
      ) : rows.length === 0 ? (
        <EmptyState title="No matching activity" subtitle="Try widening your filters or date range." />
      ) : (
        <>
          <div className="text-xs text-muted-foreground mb-2" data-testid="audit-total">{total} {total === 1 ? "entry" : "entries"}</div>
          <div className="space-y-2" data-testid="audit-list">
            {rows.map((r) => {
              const m = ACTION_META[r.action] || { label: r.action, icon: ShieldCheck, cls: "text-muted-foreground bg-secondary" };
              const Icon = m.icon;
              return (
                <div key={r.id} data-testid={`audit-row-${r.id}`} className="bg-card border border-border rounded-md p-3.5 flex items-start gap-3">
                  <div className={`shrink-0 h-9 w-9 rounded-md flex items-center justify-center ${m.cls}`}><Icon size={18} weight="bold" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{m.label}</span>
                      <span className="text-sm text-foreground">{r.summary}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">by {r.actor_name}{r.actor_email ? ` · ${r.actor_email}` : ""}</div>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}</div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-4">
              <Button variant="outline" data-testid="audit-load-more" disabled={loading} onClick={() => load(rows.length)}>
                {loading ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RetentionPanel() {
  const [settings, setSettings] = useState(null);
  const [purgeDays, setPurgeDays] = useState("90");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setSettings((await api.get("/audit/settings")).data); } catch {} };
  useEffect(() => { load(); }, []);

  const saveRetention = async (val) => {
    setBusy(true);
    try {
      await api.put("/audit/settings", { retention_days: Number(val) });
      toast.success("Retention updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const purge = async () => {
    const days = Number(purgeDays);
    if (!days || days < 1) return toast.error("Enter a number of days (1+)");
    if (!window.confirm(`Permanently delete audit entries older than ${days} days? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/audit/purge", { older_than_days: days });
      toast.success(`Purged ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!settings) return null;
  return (
    <div className="bg-card border border-border rounded-md p-4 mb-5 flex flex-wrap items-end gap-x-8 gap-y-4" data-testid="audit-retention">
      <div>
        <div className="overline text-muted-foreground mb-1">Retention</div>
        <div className="flex items-center gap-2">
          <Select value={String(settings.retention_days)} onValueChange={saveRetention} disabled={busy}>
            <SelectTrigger data-testid="audit-retention-select" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {settings.options.map((o) => <SelectItem key={o} value={String(o)} data-testid={`audit-retention-${o}`}>{RETENTION_LABELS[o] || `${o} days`}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">Entries older than this are auto-purged daily. {settings.total_entries} stored.</div>
      </div>
      <div>
        <div className="overline text-muted-foreground mb-1">Manual purge</div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Delete older than</span>
          <Input data-testid="audit-purge-days" type="number" min="1" value={purgeDays} onChange={(e) => setPurgeDays(e.target.value)} className="w-20" />
          <span className="text-sm text-muted-foreground">days</span>
          <Button variant="outline" className="text-alert hover:text-alert" data-testid="audit-purge-btn" disabled={busy} onClick={purge}>
            <Trash size={15} className="mr-1.5" /> Purge
          </Button>
        </div>
      </div>
    </div>
  );
}

function BasicUpgradeBanner({ onBuy, busy, pricing }) {
  const p = pricing?.addons?.[0];
  const mo = p ? p.monthly : 24.75;
  return (
    <div className="bg-gradient-to-r from-primary to-[#16406a] text-white rounded-lg p-5 mb-5 flex flex-wrap items-center justify-between gap-4" data-testid="audit-basic-banner">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-gold/25 flex items-center justify-center shrink-0"><ShieldCheck size={22} weight="fill" className="text-gold" /></div>
        <div>
          <div className="font-head font-semibold">You're on Basic audit (included with Enterprise)</div>
          <p className="text-sm text-white/75 max-w-xl mt-0.5">
            Upgrade to the full add-on to unlock search, date filters, CSV &amp; PDF export and configurable retention —
            just <b>${mo}/mo</b> for Enterprise (25% of standard).
          </p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button className="bg-gold text-primary hover:bg-gold/90" data-testid="audit-upgrade-monthly" disabled={busy === "monthly"} onClick={() => onBuy("monthly")}>
          {busy === "monthly" ? "Redirecting…" : `Upgrade — $${mo}/mo`}
        </Button>
        <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="audit-upgrade-yearly" disabled={busy === "yearly"} onClick={() => onBuy("yearly")}>
          {busy === "yearly" ? "Redirecting…" : "Pay yearly"}
        </Button>
      </div>
    </div>
  );
}

function Upsell({ onBuy, busy, pricing }) {
  const p = pricing?.addons?.[0];
  const mo = p ? p.monthly : 99;
  const yr = p ? p.yearly : 1039.5;
  const ent = pricing?.is_enterprise;
  return (
    <div>
      <PageHeader overline="Security · Add-on" title="Audit Log">
        Audit &amp; Compliance is a premium add-on.
      </PageHeader>
      <div className="max-w-xl bg-card border border-gold ring-1 ring-gold/40 rounded-lg p-8 text-center mx-auto" data-testid="audit-upsell">
        <div className="h-14 w-14 rounded-xl bg-gold/15 flex items-center justify-center mx-auto"><ShieldCheck size={30} weight="fill" className="text-primary" /></div>
        <h2 className="font-head font-bold text-2xl mt-4">Audit &amp; Compliance add-on</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          Unlock the full audit trail — every sign-in, review, deletion and billing change — with search, date filters,
          CSV &amp; PDF export and configurable retention (auto-purge + manual).
        </p>
        {ent && <div className="text-xs text-primary font-medium mt-3">Enterprise pricing — 25% of standard</div>}
        <div className="flex items-baseline justify-center gap-1 mt-6">
          <span className="font-head font-bold text-4xl">${mo}</span>
          <span className="text-muted-foreground">/mo</span>
          <span className="text-muted-foreground mx-2">or</span>
          <span className="font-head font-bold text-2xl">${yr}</span>
          <span className="text-muted-foreground">/yr</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Button data-testid="audit-buy-monthly" disabled={busy === "monthly"} onClick={() => onBuy("monthly")}>
            <CreditCard size={16} className="mr-1.5" /> {busy === "monthly" ? "Redirecting…" : `Add monthly — $${mo}/mo`}
          </Button>
          <Button variant="outline" data-testid="audit-buy-yearly" disabled={busy === "yearly"} onClick={() => onBuy("yearly")}>
            {busy === "yearly" ? "Redirecting…" : "Add yearly — save ~12%"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-4">Only the organization admin can add this. Cancel anytime from Manage billing.</p>
      </div>
    </div>
  );
}
